import React, { FC, useEffect, useState } from 'react';
import { GetOrganisationQuery } from '../queries/GetOrganisation.generated';
import {
  useGetOrganisationExposureLimitsQuery,
  GetOrganisationExposureLimitsQuery,
} from './GetOrganisationExposureLimits.generated';
import SearchInput from 'components/SearchInput';
import { HeroIconBuilding, useWrappedUrqlMutation } from 'toolbox';
import Input from 'components/Input';
import Checkbox from 'components/Checkbox';
import {
  useAddOrganisationBankPermissionMutation,
  useRemoveOrganisationBankPermissionMutation,
} from './SetOrganisationBankPermission.generated';
import Button from 'components/Button';
import CardWithAside from 'components/Card/CardWithAside';

interface IOrgExposureLimits {
  organisation: GetOrganisationQuery['organisations_by_pk'];
}

export const OrgExposureLimits: FC<IOrgExposureLimits> = ({ organisation }) => {
  const [filter, setFilter] = useState('');
  const [query, refreshData] = useGetOrganisationExposureLimitsQuery({
    variables: { organisation_id: organisation?.id },
    pause: !organisation,
  });

  const [editableLimits, setEditableLimits] = useState<
    | []
    | GetOrganisationExposureLimitsQuery['organisations_by_pk']['organisation_bank_exposure_limits']
  >([]);

  const addBankPermission = useWrappedUrqlMutation(
    useAddOrganisationBankPermissionMutation,
    {
      onSuccess: (result) => {
        // console.log(result);
        refreshData();
      },
      onError: (error) => {
        console.warn(error);
      },
    },
  );

  const removeBankPermission = useWrappedUrqlMutation(
    useRemoveOrganisationBankPermissionMutation,
    {
      onSuccess: (result) => {
        refreshData();
      },
      onError: (error) => {
        console.warn(error);
      },
    },
  );

  useEffect(() => {
    if (!query.fetching && !query.error) {
      // set editable text inputs
      setEditableLimits(
        query?.data?.organisations_by_pk?.organisation_bank_exposure_limits,
      );

      // create default limits if any are missing for this organisation
      const objects = checkForAndCreateMissingExposureLimits(
        organisation?.id,
        query?.data?.organisations_by_pk?.organisation_bank_exposure_limits,
        query?.data?.organisations_by_pk?.banks,
      );

      const distinctObjects = Array.from(
        new Set(objects.map((s) => s?.bank_id)),
      )
        .map((bank_id) => ({
          bank_id,
          organisation_id: objects.find((s) => s.bank_id === bank_id)
            .organisation_id,
          exposure_limit: 100,
        }))
        .sort((a, b) => (a?.bank_id > b?.bank_id ? 1 : -1));

      if (objects.length > 0) {
        addBankPermission.dispatch({
          org_bank_exp_objects: distinctObjects,
          org_banks_objects: [],
        } as any);
      }
    }
  }, [query]);

  return (
    <CardWithAside
      title="Banks & Exposure Limits"
      aside={
        <>
          <SearchInput
            placeholder="Filter"
            value={filter}
            onResetClick={() => setFilter('')}
            onChange={(event) => {
              setFilter(event.target.value);
            }}
          />
          <p className="mt-4 text-sm">
            Banks that are visible to this Organisation. Changes to permissions
            are applied immediately.
          </p>
          <p className="mt-4 text-sm">
            Banks that are visible to this Organisation, and associated Exposure
            Limits. Changes are applied immediately.
          </p>
        </>
      }
      children={
        <div className="flex flex-wrap -m-1 wrapper-fixed-height">
          {query?.data?.banks
            ?.filter((bank) =>
              bank?.name?.toLowerCase().includes(filter.toLowerCase()),
            )
            ?.map((bank, i) => {
              delete bank?.__typename;

              const enabled =
                query?.data?.organisations_by_pk?.banks?.some(
                  (b) => bank?.id == b?.bank?.id,
                ) || false;

              const toggleBankPermission = async (bank_id) => {
                if (
                  query?.data?.organisations_by_pk?.banks?.some(
                    ({ bank }) => bank?.id === bank_id,
                  )
                )
                  await removeBankPermission.dispatch({
                    bank_id,
                    organisation_id: organisation?.id,
                  });
                else
                  await addBankPermission.dispatch({
                    org_banks_objects: [
                      {
                        bank_id,
                        organisation_id: organisation?.id,
                      },
                    ],
                    org_bank_exp_objects: [
                      {
                        bank_id,
                        organisation_id: organisation?.id,
                        exposure_limit: 100,
                      },
                    ],
                  });
              };

              return (
                <div
                  key={i}
                  className={`${
                    enabled ? 'bg-jcap-100' : 'bg-gray-100'
                  } w-full mb-2 mr-2 py-2 pl-2 rounded flex items-center justify-between cursor-pointer`}
                >
                  <div
                    className="inline-flex items-center w-8 h-8 mr-3"
                    onClick={() => toggleBankPermission(bank?.id)}
                  >
                    {bank?.image?.public_url ? (
                      <img
                        src={bank?.image?.public_url}
                        alt={bank?.name}
                        title={bank?.name}
                        style={{
                          filter: enabled
                            ? undefined
                            : 'grayscale(100%) opacity(0.5)',
                        }}
                      />
                    ) : (
                      <HeroIconBuilding className="w-full h-full p-2 text-gray-400 fill-current" />
                    )}
                  </div>
                  <div
                    className={`${
                      enabled ? 'text-jcap-700' : 'text-gray-500'
                    } flex-grow mr-2 font-semibold truncate select-none`}
                    onClick={() => toggleBankPermission(bank?.id)}
                  >
                    {bank.name}
                  </div>
                  <div className="flex items-center justify-end flex-shrink-0 w-32 h-8">
                    {enabled && (
                      <>
                        <span className="mr-2 text-right text-2xs text-jcap-600">
                          Exposure Limit %
                        </span>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          className="w-20 h-8 -mb-4 bg-white border border-gray-200 pointer-events-auto"
                          value={
                            editableLimits?.find(
                              (i: any) => !i?.bank || i?.bank?.id == bank?.id,
                            )?.exposure_limit || ''
                          }
                          onChange={(e) =>
                            e.target.value <= 100 &&
                            e.target.value >= 0 &&
                            setEditableLimits([
                              ...editableLimits.filter(
                                (i: any) =>
                                  !i?.bank || i?.bank?.id !== bank?.id,
                              ),
                              {
                                id: null,
                                exposure_limit: e.target.value,
                                organisation_id: organisation?.id,
                                bank: { id: bank?.id, name: bank?.name },
                              },
                            ])
                          }
                        />
                      </>
                    )}
                    <Checkbox
                      checked={enabled}
                      onChange={() => toggleBankPermission(bank?.id)}
                      className={`ml-6 w-8 h-8 bg-white border border-gray-200`}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      }
      footer={
        <>
          <Button
            onClick={() =>
              setEditableLimits(
                query?.data?.organisations_by_pk
                  ?.organisation_bank_exposure_limits,
              )
            }
            className="btn btn-white"
          >
            Reset Limits
          </Button>
          <Button
            disabled={
              JSON.stringify(editableLimits) ===
              JSON.stringify(
                query?.data?.organisations_by_pk
                  ?.organisation_bank_exposure_limits,
              )
            }
            onClick={() => {
              addBankPermission.dispatch({
                org_bank_exp_objects: (editableLimits as Array<any>)
                  .filter((l) => l?.id === null)
                  .map((l) => ({
                    organisation_id: l?.organisation_id,
                    bank_id: l?.bank?.id,
                    exposure_limit: Number(l?.exposure_limit),
                  })),
                org_banks_objects: [],
              });
            }}
          >
            Save Limits
          </Button>
        </>
      }
    />
  );
};

const checkForAndCreateMissingExposureLimits = (
  organisation_id: number,
  org_limits: GetOrganisationExposureLimitsQuery['organisations_by_pk']['organisation_bank_exposure_limits'],
  org_banks: GetOrganisationExposureLimitsQuery['organisations_by_pk']['banks'],
): any[] => {
  const objects = [];

  org_banks.map(({ bank }) => {
    if (!org_limits.some((limit) => limit?.bank?.id === bank?.id)) {
      // console.log(`default limit missing for ${bank.name}, creating...`);
      objects.push({ bank_id: bank?.id, organisation_id });
    }
  });

  return objects;
};
